import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGet, adminPatch, formatApiError } from "../../lib/adminApi";
import { PageHeader, StatusBadge, LoadingRow, EmptyRow, ErrorBanner, OvergeslagenBanner } from "../../components/admin/AdminUI";
import { Button } from "../../components/fulkra";
import { OnlineBanner } from "../../components/aanmetingen/OnlineBanner";
import { NoShowModal, VerzetModal } from "../../components/aanmetingen/NoShowVerzetModals";

function hasLocalDraft(aanmeetdagId, werknemerId) {
  try { return Boolean(window.localStorage.getItem(`motera:aanmeting:${aanmeetdagId}:${werknemerId}`)); }
  catch { return false; }
}

const KOLOMKOP = {
  textAlign: "left", padding: "var(--ruimte-4) var(--ruimte-5)",
  fontSize: "var(--fs-xs)", letterSpacing: "var(--ls-label)", textTransform: "uppercase",
  fontWeight: "var(--fw-medium)", color: "var(--text-soft)",
  borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap",
};
const CEL = { padding: "var(--ruimte-5)", color: "var(--text-body)" };

export default function AanmeetdagWerknemersPage() {
  const { aanmeetdag_id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeAction, setActiveAction] = useState(null); // { type, uitvoering, werknemer }

  const dagQ = useQuery({
    queryKey: ["aanmeetdag", aanmeetdag_id],
    queryFn: () => adminGet(`/api/admin/aanmeetdagen/${aanmeetdag_id}`),
  });
  const uQ = useQuery({
    queryKey: ["uitvoeringen-dag", aanmeetdag_id],
    queryFn: () => adminGet(`/api/admin/aanmeetdagen/${aanmeetdag_id}/uitvoeringen`),
    refetchInterval: () => (document.hidden ? false : 30000),
  });

  const startMut = useMutation({
    mutationFn: (uid) => adminPatch(`/api/admin/uitvoeringen/${uid}/start`, {}),
    onSuccess: (_, uid) => {
      qc.invalidateQueries({ queryKey: ["uitvoeringen-dag", aanmeetdag_id] });
      const item = (uQ.data?.items || []).find((x) => x.uitvoering_id === uid);
      if (item) navigate(`/aanmetingen/dag/${aanmeetdag_id}/werknemer/${item.werknemer_id}`);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const noShowMut = useMutation({
    mutationFn: ({ uid, body }) => adminPatch(`/api/admin/uitvoeringen/${uid}/no-show`, body),
    onSuccess: () => { toast.success("No-show geregistreerd"); setActiveAction(null); qc.invalidateQueries({ queryKey: ["uitvoeringen-dag", aanmeetdag_id] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const verzetMut = useMutation({
    mutationFn: ({ uid, body }) => adminPatch(`/api/admin/uitvoeringen/${uid}/verzet`, body),
    onSuccess: () => { toast.success("Verzet"); setActiveAction(null); qc.invalidateQueries({ queryKey: ["uitvoeringen-dag", aanmeetdag_id] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const dag = dagQ.data;
  const items = uQ.data?.items || uQ.data || [];
  const totaal = items.length;
  const klaar = items.filter((u) => u.status === "voltooid" || u.status === "no_show" || u.status === "verzet").length;
  const progress = totaal ? Math.round((klaar / totaal) * 100) : 0;

  function handleStart(u) {
    if (hasLocalDraft(aanmeetdag_id, u.werknemer_id)) {
      const cont = window.confirm("Onafgemaakte aanmeting hervatten?\nOK = Hervatten   Annuleren = Opnieuw beginnen");
      if (!cont) {
        try { window.localStorage.removeItem(`motera:aanmeting:${aanmeetdag_id}:${u.werknemer_id}`); } catch { /* ignore */ }
      }
    }
    if (u.status === "gepland") startMut.mutate(u.uitvoering_id);
    else navigate(`/aanmetingen/dag/${aanmeetdag_id}/werknemer/${u.werknemer_id}`);
  }

  return (
    <div className="fulkra" style={{ minHeight: "100vh", background: "var(--surface-page)" }}>
      <OnlineBanner />
      <div style={{ margin: "0 auto", maxWidth: 1120, padding: "var(--ruimte-8) var(--ruimte-7)" }} data-testid="aanmeetdag-werknemers-page">
        <Link
          to="/aanmetingen"
          data-testid="back-to-dagen"
          style={{ display: "inline-block", marginBottom: "var(--ruimte-5)", fontSize: "var(--fs-body)", color: "var(--text-action)" }}
        >
          ← Terug naar aanmeetdagen
        </Link>

        <PageHeader
          title={dag ? `${dag.datum} — ${dag.aanmeet_locatie}` : "Aanmeetdag"}
          subtitle={dag ? `Werkgever: ${dag.werkgever_naam || "—"}` : undefined}
        />

        <div
          data-testid="dag-progress"
          style={{
            marginBottom: "var(--ruimte-7)", background: "var(--wit)",
            border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-vlak)",
            boxShadow: "var(--elevation-card)", padding: "var(--ruimte-5) var(--ruimte-6)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "var(--fs-body)" }}>
            <span style={{ color: "var(--text-body)" }}>{klaar} van {totaal} klaar</span>
            <span style={{ fontWeight: "var(--fw-semi)", color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>{progress}%</span>
          </div>
          <div
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{
              marginTop: "var(--ruimte-4)", height: 12, width: "100%", overflow: "hidden",
              borderRadius: "var(--radius-pil)", background: "var(--neutraal-100)",
            }}
          >
            <div style={{ height: "100%", width: `${progress}%`, background: "var(--positief)", transition: "width var(--duur-traag) var(--ease-standaard)" }} />
          </div>
        </div>

        <ErrorBanner error={uQ.error || dagQ.error} />
        {/* Van alle plekken waar een rij stil mag verdwijnen is de dagplanning de
            slechtste: een persoon die hier niet op de werklijst staat, wordt straks wel
            verwacht. En een dagindeling met een tijdslot minder ziet er precies zo uit
            als een dag die minder sloten heeft. */}
        <OvergeslagenBanner
          aantal={uQ.data?.overgeslagen} enkelvoud="aanmeting" meervoud="aanmetingen"
          testid="dag-uitvoeringen-overgeslagen"
        />
        <OvergeslagenBanner
          aantal={dagQ.data?.tijdsloten_overgeslagen} enkelvoud="tijdslot" meervoud="tijdsloten"
          testid="dag-tijdsloten-overgeslagen"
        />

        <div
          style={{
            overflowX: "auto", background: "var(--wit)", border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-vlak)", boxShadow: "var(--elevation-card)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-body)" }} data-testid="uitvoeringen-table">
            <thead style={{ background: "var(--surface-sunken)" }}>
              <tr>
                {["Klantnr.", "Naam", "Status"].map((k) => (
                  <th key={k} scope="col" style={KOLOMKOP}>{k}</th>
                ))}
                <th scope="col" style={{ ...KOLOMKOP, textAlign: "right" }}>Acties</th>
              </tr>
            </thead>
            <tbody>
              {uQ.isLoading ? <LoadingRow cols={4} /> :
                items.length === 0 ? <EmptyRow cols={4} label="Geen werknemers toegewezen aan deze dag" /> :
                items.map((u) => (
                  <tr key={u.uitvoering_id} data-testid={`uitvoering-row-${u.uitvoering_id}`} style={{ borderTop: "1px solid var(--neutraal-100)" }}>
                    <td style={{ ...CEL, fontFamily: "var(--font-mono)", fontSize: "var(--fs-sm)" }}>{u.klantnummer || "—"}</td>
                    <td style={{ ...CEL, color: "var(--text-strong)" }}>{u.werknemer_naam || "—"}</td>
                    <td style={CEL}><StatusBadge value={u.status} testid={`uitvoering-status-${u.uitvoering_id}`} /></td>
                    <td style={{ ...CEL, textAlign: "right" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: "var(--ruimte-4)" }}>
                        {u.status === "gepland" || u.status === "in_uitvoering" ? (
                          <>
                            <Button
                              size="lg"
                              onClick={() => handleStart(u)}
                              data-testid={`btn-start-${u.uitvoering_id}`}
                              disabled={startMut.isPending}
                            >
                              {u.status === "in_uitvoering" ? "Hervat" : "Start aanmeting"}
                            </Button>
                            <Button
                              size="lg"
                              variant="secundair"
                              onClick={() => setActiveAction({ type: "no_show", u })}
                              data-testid={`btn-no-show-${u.uitvoering_id}`}
                              style={{ borderColor: "var(--alarm-rand)", color: "var(--text-alarm)" }}
                            >
                              No-show
                            </Button>
                            <Button
                              size="lg"
                              variant="secundair"
                              onClick={() => setActiveAction({ type: "verzet", u })}
                              data-testid={`btn-verzet-${u.uitvoering_id}`}
                            >
                              Verzet
                            </Button>
                          </>
                        ) : (
                          <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {activeAction?.type === "no_show" ? (
          <NoShowModal werknemerNaam={activeAction.u.werknemer_naam || ""}
            onClose={() => setActiveAction(null)}
            onSubmit={(body) => noShowMut.mutate({ uid: activeAction.u.uitvoering_id, body })}
            loading={noShowMut.isPending} />
        ) : null}
        {activeAction?.type === "verzet" ? (
          <VerzetModal werknemerNaam={activeAction.u.werknemer_naam || ""}
            currentAanmeetdagId={aanmeetdag_id}
            onClose={() => setActiveAction(null)}
            onSubmit={(body) => verzetMut.mutate({ uid: activeAction.u.uitvoering_id, body })}
            loading={verzetMut.isPending} />
        ) : null}
      </div>
    </div>
  );
}
